import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {LedgerSyncModalService} from '@shared/ledger-sync-modal/service.service';
import {LocalStorageService} from '@services/local-storage.service';

const NEW_UI_PATH = '/ui/next';

/**
 * The switch to the new design. nginx serves this app or the new one at the same URLs, picked by
 * the `ls_ui` cookie, which `/ui/next` and `/ui/old` set and clear. Whether the user was already
 * asked is kept here instead, in localStorage — `/ui/old` deletes the cookie, so it can't tell
 * someone who just came back from the new design from someone who's never seen it. Both designs
 * share the origin, so either answer sticks across switching back and forth.
 */
@Injectable({
  providedIn: 'root'
})
export class UiSwitchService {
  constructor(
    private ledgerSyncModalService: LedgerSyncModalService,
    private localStorageService: LocalStorageService,
    private translateService: TranslateService,
  ) {
  }

  /** A real page load: the server sets the cookie, then sends the browser back to the app. */
  switchToNew() {
    window.location.href = NEW_UI_PATH;
  }

  /** Asks whether to try the new design — shown once per browser, whatever the answer (Settings
   *  has the switch for later), so someone back from the new design isn't asked again. Staff
   *  signed in on behalf of a customer are asked too: the switch is their browser's, not the
   *  customer's. Someone who was asked before, on an earlier visit, gets the new-dashboard notice
   *  instead (once); someone asked for the first time now skips it, as the invitation already
   *  covers the whole new design. */
  offerNewDesign(isClient: boolean) {
    if (this.localStorageService.isNewDesignOffered()) {
      this.announceNewDashboard(isClient);
      return;
    }
    const t = (key: string) => this.translateService.instant(key);
    const modalRef = this.ledgerSyncModalService.show({
      header: t('Try the new LedgerSync design'),
      message: t('LedgerSync has a new design. You can switch any time later in Settings, and back again from the new design\'s Settings.'),
      footer: true,
      buttons: [
        {caption: t('Not now'), variant: 'secondary', callback: (ref) => ref.hide()},
        {caption: t('Try the new design'), callback: () => this.switchToNew()},
      ],
    });
    if (modalRef) {
      this.localStorageService.setNewDesignOffered();
      this.localStorageService.setNewDashboardAnnounced();
    }
  }

  /** The new design's main page was rebuilt. Clients don't have that dashboard, so they're skipped. */
  private announceNewDashboard(isClient: boolean) {
    if (isClient || this.localStorageService.isNewDashboardAnnounced()) {
      return;
    }
    const t = (key: string) => this.translateService.instant(key);
    const modalRef = this.ledgerSyncModalService.show({
      header: t('A new dashboard in the new design'),
      message: t('We rebuilt the main page of the new LedgerSync design. The new dashboard shows how the bank connections of all your clients are doing and what needs your attention, in one place. You can switch back any time from the new design\'s Settings.'),
      footer: true,
      buttons: [
        {caption: t('Not now'), variant: 'secondary', callback: (ref) => ref.hide()},
        {caption: t('Take a look'), callback: () => this.switchToNew()},
      ],
    });
    if (modalRef) {
      this.localStorageService.setNewDashboardAnnounced();
    }
  }
}
